import { Check, ArrowRight, Video } from "lucide-react";
import { Reveal, SectionHeading, scrollToSection } from "./Reveal";
import { PRICING, CONSULTATION_RATE } from "../data/content";

export default function Pricing() {
  return (
    <section id="pricing" className="py-20 sm:py-28 lg:py-32 bg-white scroll-mt-16" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <SectionHeading
          align="center"
          eyebrow="Engagements"
          title="Start with an hour. Scale into a transformation."
          subtitle="Online consultation is booked by the hour. Deeper project engagements are scoped and quoted on your free discovery call, so the scope fits your brand."
        />

        <Reveal delay={0.1}>
          <div
            className="mt-14 rounded-[28px] bg-[#161617] text-white p-8 sm:p-12 grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center shadow-[0_24px_60px_rgba(0,0,0,0.25)]"
            data-testid="pricing-featured-rate"
          >
            <div>
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold bg-[#d4af37] text-[#161617] px-3 py-1 rounded-full">
                Consultation rate
              </span>
              <h3 className="mt-5 text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-3">
                <Video size={22} className="text-[#d4af37]" /> {CONSULTATION_RATE.name}
              </h3>
              <p className="mt-3 text-[#c7c7cc] leading-relaxed max-w-xl">{CONSULTATION_RATE.desc}</p>
              <ul className="mt-6 grid sm:grid-cols-2 gap-2.5 max-w-xl">
                {CONSULTATION_RATE.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[15px] text-white/90">
                    <Check size={17} className="mt-0.5 shrink-0 text-[#d4af37]" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center lg:text-right lg:pl-10 lg:border-l border-white/10">
              <p className="text-5xl sm:text-6xl font-semibold tracking-tight" data-testid="pricing-rate-value">{CONSULTATION_RATE.price}</p>
              <p className="text-[#a1a1a6] mt-2">{CONSULTATION_RATE.unit}</p>
              <button
                data-testid="pricing-cta-hourly"
                onClick={() => scrollToSection("#booking")}
                className="mt-7 px-7 py-3.5 rounded-full bg-white text-[#1d1d1f] font-medium text-sm hover:bg-[#e8e8ed] transition-all active:scale-[0.98] inline-flex items-center gap-2"
              >
                Book an hour <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-6 grid lg:grid-cols-3 gap-6 items-stretch">
          {PRICING.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={`pricing-card-${tier.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className={`h-full flex flex-col rounded-[28px] p-8 transition-all duration-300 ${
                  tier.popular
                    ? "bg-[#f5f5f7] border-2 border-[#0066cc]/40 shadow-[0_12px_36px_rgba(0,102,204,0.10)]"
                    : "bg-[#f5f5f7] border border-[#e5e5e7] hover:shadow-[0_16px_40px_rgba(0,0,0,0.07)]"
                }`}
              >
                {tier.popular && (
                  <span className="self-start text-[11px] uppercase tracking-[0.18em] font-semibold bg-[#0066cc] text-white px-3 py-1 rounded-full">
                    Most chosen
                  </span>
                )}
                <h3 className={`text-xl font-semibold tracking-tight ${tier.popular ? "mt-4" : ""}`}>{tier.name}</h3>
                <p className="mt-1 text-sm text-[#86868b]">{tier.period}</p>
                <p className="mt-4 text-sm font-medium text-[#0066cc]">Scope &amp; quote on your discovery call</p>
                <p className="mt-3 text-[15px] leading-relaxed text-[#86868b]">{tier.desc}</p>
                <ul className="mt-6 flex-1 flex flex-col gap-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px]">
                      <Check size={17} className={`mt-0.5 shrink-0 ${tier.popular ? "text-[#0066cc]" : "text-[#1d1d1f]/60"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${tier.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  onClick={() => scrollToSection("#booking")}
                  className={`mt-8 px-6 py-3.5 rounded-full font-medium text-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2 ${
                    tier.popular
                      ? "bg-[#0066cc] text-white hover:bg-[#0077ed]"
                      : "bg-white border border-[#d2d2d7] text-[#1d1d1f] hover:bg-[#e8e8ed]"
                  }`}
                >
                  Discuss on a discovery call <ArrowRight size={15} />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-8 text-center text-xs text-[#a1a1a6]">
            Online consultation: ₹7,000 per hour · Project engagements quoted after the free discovery call.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
